/**
 * Trójstronne scalanie zmian w formularzu.
 *
 * Dwie osoby otwierają ten sam rekord (stan wyjściowy — `baza`). Jedna zapisuje
 * (`ich` — bieżący stan na serwerze), druga ma w formularzu swoje zmiany (`moje`).
 * Zamiast odrzucać zapis komunikatem „ktoś był szybszy”, porównujemy trzy stany
 * pole po polu:
 *
 *   - ja nie ruszałem pola          → biorę wartość z serwera,
 *   - nikt poza mną go nie ruszał   → zostaje moja,
 *   - obaj zmieniliśmy je tak samo  → bez problemu,
 *   - obaj zmieniliśmy inaczej      → KONFLIKT: człowiek wybiera, które zostaje.
 *
 * Listy pozycji (np. zestaw) można scalać po tożsamości elementów (`klucze`):
 * pozycje dodane przez obie strony zostają obie, usunięcie i zmiana różnych
 * atrybutów tej samej pozycji też się godzą. Listy bez funkcji tożsamości
 * traktujemy jak jedną wartość.
 *
 * Moduł jest czysty (bez Reacta i DOM), dzięki czemu testuje się go w vitest.
 */
export type Formularz = Record<string, unknown>;

export interface Konflikt<F extends Formularz = Formularz> {
  /** Stabilny identyfikator konfliktu — klucz Reacta i rozstrzygania. */
  id: string;
  /** Po ludzku: „Nazwa” albo „Pozycja „Wenflon”: ilość”. */
  etykieta: string;
  opisMoje: string;
  opisIch: string;
  /** Wpisuje do formularza wartość drugiej osoby (wybór „biorę jego”). */
  przejmijIch: (dane: F) => F;
}

export interface OpcjeScalania<F extends Formularz = Formularz> {
  /** Nazwy pól do komunikatów; brak wpisu = nazwa pola. */
  etykiety?: Partial<Record<keyof F & string, string>>;
  /** Tożsamość elementów list: pole → funkcja zwracająca klucz elementu. */
  klucze?: Partial<Record<keyof F & string, (element: any) => string>>; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Jak nazwać element listy w komunikacie (domyślnie jego klucz). */
  opisElementu?: Partial<Record<keyof F & string, (element: any) => string>>; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Atrybuty elementów list, których nie porównujemy (np. lokalny klucz Reacta). */
  pomijaneAtrybuty?: string[];
  /** Nazwy atrybutów elementów list w komunikatach (domyślnie nazwa techniczna). */
  etykietyAtrybutow?: Record<string, string>;
  /**
   * Wartość pola albo atrybutu po ludzku — np. numer dostawcy zamieniony na jego nazwę.
   * Brak funkcji lub zwrócone `undefined` = domyślny zapis.
   */
  pokazWartosc?: (pole: string, wartosc: unknown) => string | undefined;
}

export interface WynikScalania<F extends Formularz = Formularz> {
  /** Propozycja formularza: przy konflikcie zostaje wartość „moja”. */
  wynik: F;
  konflikty: Konflikt<F>[];
  /** Pola (etykiety), w których przejęto cudzą zmianę. */
  cudze: string[];
}

// --------------------------------------------------------------------------- pomocnicze

function stabilny(v: unknown): string {
  if (v === undefined) return 'undefined';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stabilny).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${stabilny(o[k])}`).join(',')}}`;
}

export const rowne = (a: unknown, b: unknown): boolean => stabilny(a) === stabilny(b);

/** Wartość do pokazania człowiekowi w oknie konfliktu. */
export function pokaz(v: unknown): string {
  if (v === undefined || v === null || v === '') return '(puste)';
  if (v === true) return 'tak';
  if (v === false) return 'nie';
  if (Array.isArray(v)) return v.length === 0 ? '(brak pozycji)' : `${v.length} poz.`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

type Elem = Record<string, unknown>;

/** Indeks listy po kluczu; powtórzony klucz dostaje przyrostek #n, żeby nic nie zginęło. */
function indeksuj(lista: unknown[], klucz: (e: Elem) => string): Map<string, Elem> {
  const mapa = new Map<string, Elem>();
  const licznik = new Map<string, number>();
  for (const e of lista as Elem[]) {
    const k = klucz(e);
    const n = licznik.get(k) ?? 0;
    licznik.set(k, n + 1);
    mapa.set(n === 0 ? k : `${k}#${n}`, e);
  }
  return mapa;
}

/** Lista z jednym elementem (o kluczu `k`) podmienionym, usuniętym albo dopisanym. */
function ustawElement(
  lista: unknown[], klucz: (e: Elem) => string, k: string, nowy: Elem | undefined,
): unknown[] {
  const mapa = indeksuj(lista, klucz);
  if (nowy === undefined) mapa.delete(k);
  else mapa.set(k, nowy);
  return [...mapa.values()];
}

// --------------------------------------------------------------------------- scalanie list

function scalListe<F extends Formularz>(
  pole: string,
  etykietaPola: string,
  baza: unknown[], moje: unknown[], ich: unknown[],
  klucz: (e: Elem) => string,
  opisz: (e: Elem) => string,
  pomijane: Set<string>,
  opcje: OpcjeScalania<F>,
): { lista: Elem[]; konflikty: Konflikt<F>[]; przejete: boolean } {
  const wartosc = (a: string, v: unknown) => opcje.pokazWartosc?.(a, v) ?? pokaz(v);
  const atrybut = (a: string) => opcje.etykietyAtrybutow?.[a] ?? a;
  const B = indeksuj(baza, klucz);
  const M = indeksuj(moje, klucz);
  const T = indeksuj(ich, klucz);

  const kolejnosc = [
    ...M.keys(),
    ...[...T.keys()].filter((k) => !M.has(k)),
    ...[...B.keys()].filter((k) => !M.has(k) && !T.has(k)),
  ];

  const lista: Elem[] = [];
  const konflikty: Konflikt<F>[] = [];
  let przejete = false;

  const nazwa = (e: Elem) => `${etykietaPola}: „${opisz(e)}”`;
  const wymien = (k: string, el: Elem | undefined) => (d: F): F =>
    ({ ...d, [pole]: ustawElement((d[pole] as unknown[]) ?? [], klucz, k, el) });

  for (const k of kolejnosc) {
    const b = B.get(k);
    const m = M.get(k);
    const t = T.get(k);

    if (m && t) {
      if (!b) {
        // Dodane naraz przez obie strony.
        if (!rowne(bezPomijanych(m, pomijane), bezPomijanych(t, pomijane))) {
          konflikty.push({
            id: `${pole}:${k}`, etykieta: `${nazwa(m)} — dodano z różnymi wartościami`,
            opisMoje: opiszElement(m, pomijane), opisIch: opiszElement(t, pomijane),
            przejmijIch: wymien(k, t),
          });
        }
        lista.push(m);
        continue;
      }
      // Ta sama pozycja u wszystkich — scalamy atrybuty.
      const el: Elem = { ...m };
      for (const a of new Set([...Object.keys(b), ...Object.keys(m), ...Object.keys(t)])) {
        if (pomijane.has(a)) continue;
        if (rowne(m[a], b[a])) {
          if (!rowne(t[a], b[a])) { el[a] = t[a]; przejete = true; }
        } else if (!rowne(t[a], b[a]) && !rowne(t[a], m[a])) {
          konflikty.push({
            id: `${pole}:${k}:${a}`, etykieta: `${nazwa(m)} — ${atrybut(a)}`,
            opisMoje: wartosc(a, m[a]), opisIch: wartosc(a, t[a]),
            przejmijIch: (d) => {
              const biezacy = indeksuj((d[pole] as unknown[]) ?? [], klucz).get(k);
              return biezacy ? wymien(k, { ...biezacy, [a]: t[a] })(d) : d;
            },
          });
        }
      }
      lista.push(el);
    } else if (m && !t) {
      if (!b) { lista.push(m); continue; }                       // dodana przeze mnie
      if (rowne(bezPomijanych(m, pomijane), bezPomijanych(b, pomijane))) { przejete = true; continue; } // oni ją usunęli
      lista.push(m);                                             // usunęli to, co ja zmieniłem
      konflikty.push({
        id: `${pole}:${k}:usunieta`, etykieta: `${nazwa(m)} — usunięta przez drugą osobę`,
        opisMoje: `zmieniona: ${opiszElement(m, pomijane)}`, opisIch: 'usunięta',
        przejmijIch: wymien(k, undefined),
      });
    } else if (!m && t) {
      if (!b) { lista.push(t); przejete = true; continue; }      // dodana przez nich
      if (rowne(bezPomijanych(t, pomijane), bezPomijanych(b, pomijane))) continue; // ja ją usunąłem, oni nie ruszali
      konflikty.push({                                           // usunąłem to, co oni zmienili
        id: `${pole}:${k}:usunieta-przeze-mnie`, etykieta: `${nazwa(t)} — usunięta przez Ciebie`,
        opisMoje: 'usunięta', opisIch: `zmieniona: ${opiszElement(t, pomijane)}`,
        przejmijIch: wymien(k, t),
      });
    }
  }
  return { lista, konflikty, przejete };
}

function bezPomijanych(e: Elem, pomijane: Set<string>): Elem {
  return Object.fromEntries(Object.entries(e).filter(([k]) => !pomijane.has(k)));
}

function opiszElement(e: Elem, pomijane: Set<string>): string {
  return Object.entries(bezPomijanych(e, pomijane))
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${pokaz(v)}`).join(', ');
}

// --------------------------------------------------------------------------- scalanie formularza

export function scal<F extends Formularz>(
  baza: F, moje: F, ich: F, opcje: OpcjeScalania<F> = {},
): WynikScalania<F> {
  const wynik: Formularz = { ...moje };
  const konflikty: Konflikt<F>[] = [];
  const cudze: string[] = [];
  const pomijane = new Set(opcje.pomijaneAtrybuty ?? []);

  // Listy pozycji porównujemy bez atrybutów lokalnych (np. klucza Reacta): po zapisie
  // serwer nadaje pozycjom nowe identyfikatory, a treść zostaje ta sama.
  const rw = (x: unknown, y: unknown): boolean => {
    const bez = (v: unknown) => (Array.isArray(v)
      ? v.map((e) => (e && typeof e === 'object' ? bezPomijanych(e as Elem, pomijane) : e))
      : v);
    return rowne(bez(x), bez(y));
  };

  const klucze = new Set<string>([...Object.keys(baza), ...Object.keys(moje), ...Object.keys(ich)]);
  for (const k of klucze) {
    const etykieta = (opcje.etykiety as Record<string, string> | undefined)?.[k] ?? k;
    const b = baza[k];
    const m = moje[k];
    const t = ich[k];

    if (rw(m, b)) {
      if (!rw(t, b)) { wynik[k] = t; cudze.push(etykieta); }
      continue;
    }
    if (rw(t, b) || rw(t, m)) continue;

    const klucz = (opcje.klucze as Record<string, (e: Elem) => string> | undefined)?.[k];
    if (klucz && Array.isArray(b) && Array.isArray(m) && Array.isArray(t)) {
      const opisz = (opcje.opisElementu as Record<string, (e: Elem) => string> | undefined)?.[k] ?? klucz;
      const s = scalListe<F>(k, etykieta, b, m, t, klucz, opisz, pomijane, opcje);
      wynik[k] = s.lista;
      konflikty.push(...s.konflikty);
      if (s.przejete) cudze.push(etykieta);
      continue;
    }

    konflikty.push({
      id: k, etykieta,
      opisMoje: opcje.pokazWartosc?.(k, m) ?? pokaz(m),
      opisIch: opcje.pokazWartosc?.(k, t) ?? pokaz(t),
      przejmijIch: (d) => ({ ...d, [k]: t }),
    });
  }

  return { wynik: wynik as F, konflikty, cudze };
}
